import { Component, computed, effect, OnInit, signal, Signal, ViewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { LoadingComponent } from '../loading/loading.component';
import { Router } from '@angular/router';
import { Location, NgClass, NgStyle } from '@angular/common';
import { SignInComponent } from './sign-in/sign-in.component';
import { SignUpComponent } from './sign-up/sign-up.component';
import { environment } from 'environment';
import { MaintenanceService } from '@services/auth/maintenance.service';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthPageService } from '@services/auth/auth-page.service';
import { AuthService } from '@services/auth/auth.service';
import { getAuth, MultiFactorError, OAuthProvider, signInWithPopup, User, UserCredential } from 'firebase/auth';
import { ToastService } from '@services/helper/toast.service';
import { SamlService } from '@services/auth/saml.service';
import { AwButtonComponent } from '@components/misc/aw-button/aw-button.component';
import { AW } from '@airwallet/types';

@Component({
    selector: 'app-auth-page',
    imports: [LoadingComponent, SignInComponent, SignUpComponent, LottieComponent, TranslateModule, NgClass, AwButtonComponent, NgStyle],
    templateUrl: './auth-page.component.html',
    styleUrl: './auth-page.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager
})
export class AuthPageComponent implements OnInit {
    @ViewChild(SignInComponent) signInComponent!: SignInComponent;
    @ViewChild(SignUpComponent) signUpComponent!: SignUpComponent;

    isDemo: boolean = environment.isDemo;
    view: WritableSignal<string> = signal('sign-in'); // Default view is 'sign-in'
    animationView: Signal<string> = computed(() => {
        const isMfa = this.authPageService.isMfaTotpSignIn() || this.authPageService.isMfaPhoneSignIn();
        const isCompanyIndividual = this.authPageService.isCompanyIndividualSignUp();
        const loading = this.authPageService.loading();

        if (this.isDemo && loading && this.view() === 'sign-up') {
            return 'demo';
        }

        if (isMfa) {
            return 'mfa';
        } else if (isCompanyIndividual) {
            // demo only accepts business by default, so we hide this view
            if (this.isDemo) {
                return this.view();
            }

            return 'companyIndividual';
        } else {
            return this.view();
        }
    });

    options: AnimationOptions = {
        // https://www.npmjs.com/package/ngx-lottie
        path: '../../assets/animations/AW_waves.json'
    };
    supportPhone: { us: { phoneNumber: string; telLink: string }; main: { phoneNumber: string; telLink: string } } = environment.supportPhone;
    version: string = environment.VERSION;
    showLoad = true;
    isUnderMaintenance: Signal<boolean> = this.maintenanceService.isUnderMaintenance;
    backAt: Signal<number> = this.maintenanceService.dashboardBackAt;
    isTransitioning: WritableSignal<boolean> = signal(false);
    currentTimeout: NodeJS.Timeout | null = null;
    private readonly auth = getAuth();

    constructor(
        private router: Router,
        private location: Location,
        protected maintenanceService: MaintenanceService,
        protected authPageService: AuthPageService,
        private authService: AuthService,
        private toastService: ToastService,
        private translate: TranslateService,
        private samlService: SamlService
    ) {
        effect(() => {
            const currentView = this.animationView();

            if (currentView) {
                this.isTransitioning.set(true);
            }

            if (this.currentTimeout) {
                clearTimeout(this.currentTimeout);
            }

            this.currentTimeout = setTimeout(() => {
                this.isTransitioning.set(false);
                this.currentTimeout = null;
            }, 400);
        });

        this.view.set((this.router.url.split('/').pop() ?? '').split('?')[0]);
    }

    async ngOnInit() {
        this.showLoad = false;
    }

    getFormattedBackAt(): string {
        if (!this.backAt()) {
            return '';
        }
        const date = new Date(this.backAt() * 1000);
        return date.toLocaleString(); // Adjust formatting as needed
    }

    toggleAuthPage() {
        if (this.view() === 'sign-in') {
            this.view.set('sign-up');
            this.location.replaceState('/sign-up');
        } else {
            this.view.set('sign-in');
            this.location.replaceState('/sign-in');
        }
    }

    goBack() {
        this.authPageService.isMfaTotpSignIn.set(false);
        this.authPageService.isMfaPhoneSignIn.set(false);
    }

    async handleSubmit(): Promise<void> {
        if (this.authPageService.isMfaTotpSignIn()) {
            console.log('Handling TOTP MFA sign-in');
            this.signInComponent.handleTotpMfa();
        } else if (this.authPageService.isMfaPhoneSignIn()) {
            console.log('Handling phone MFA sign-in');
            this.signInComponent.handlePhoneMfa();
        } else if (this.view() === 'sign-in') {
            this.signInComponent.signIn();
        } else {
            await this.signUpComponent.signUp().then(async (created: boolean) => {
                if (created) {
                    await this.authService.signOut(true); // to secure that there are not two different users signed in, because it messes up the UI in the first opened window, and the navbar is also shown in this set-password page, which it not should be.
                    this.view.set('sign-in');
                    this.location.replaceState('/sign-in');
                }
            });
        }
    }

    async signInWithSAML() {
        const provider = new OAuthProvider('saml.microsoft');
        provider.setCustomParameters({ prompt: 'consent' }); // Apply This if you want to force consent
        provider.addScope('email');
        this.authPageService.loadingSamlLogin.set(true);
        try {
            // Authenticate with SAML
            const result: UserCredential = await signInWithPopup(this.auth, provider);
            const user: User = result.user;

            const rawInfo = (result as any)._tokenResponse?.rawUserInfo;
            const parsedInfo = rawInfo ? JSON.parse(rawInfo) : null;

            const roles: AW.Account.Saml.AppRole[] = this.samlService.extractRolesFromSamlResponse(result);
            if (roles.length === 0) {
                this.toastService.error(this.translate.instant('account.sso.sso_error_code') + ' E4.');
                this.authPageService.loadingSamlLogin.set(false);
                return;
            }
            const isOwner = roles.some(role => role.role === 'owner');
            // Check if the user exists, by checking custom claims for memberships, which are only set for existing users on BE
            const idTokenResult = await user.getIdTokenResult(true); // true to force refresh
            const userExists = !!idTokenResult.claims.memberships;

            if (userExists) {
                // Memberships are put in claims only on backend, so user must exist,
                // handle post-sign-in logic
                if (!isOwner) {
                    await this.samlService.validateSamlAccess(roles);
                    await this.auth.currentUser?.getIdToken(true);
                }
                if (isOwner && roles[0].groupId.split('_operated_by_')[0] !== user.uid) {
                    this.toastService.warning(this.translate.instant('account.sso.sso_error_code') + ' E2.');
                    this.authPageService.loadingSamlLogin.set(false);
                    return;
                }
            } else {
                if (isOwner) {
                    await user.delete();
                    this.toastService.warning(this.translate.instant('account.sso.sso_error_code') + ' E1.');
                    this.authPageService.loadingSamlLogin.set(false);
                    return;
                }

                const extractedInfo: AW.Account.Saml.ExtractedInfo = this.samlService.extractInfo(parsedInfo);
                // User does not exist, create a new user
                const memberInfo: AW.Account.Saml.MemberInfo = {
                    email: user.email ?? '',
                    first_name: extractedInfo.firstName,
                    last_name: extractedInfo.lastName,
                    phone_number: extractedInfo.phoneNumber
                };
                await this.samlService.createUserWithSamlResponse(memberInfo, roles);
                await this.auth.currentUser?.getIdToken(true);
            }
            await this.samlService.removePasswordProvider();
            await this.authService.afterSigninProcess(user);
        } catch (error) {
            const authError = error as { code?: string };
            if (authError.code === 'auth/account-exists-with-different-credential') {
                this.toastService.warning(this.translate.instant('account.sso.sso_error_code') + ' E3.');
            }
            if (authError?.code === 'auth/multi-factor-auth-required') {
                // Check if Totp MFA is enabled on the account, if not, show phone MFA
                this.view.set('sign-in');
                this.location.replaceState('/sign-in');
                this.signInComponent.handleMfaError(error as MultiFactorError);
            } else {
                this.toastService.error('account.sso.sso_error');
            }
            this.authPageService.loadingSamlLogin.set(false);
            console.error('Error during SAML sign-in:', error);
        }
        this.authPageService.loadingSamlLogin.set(false);
    }
}
